import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  getSimulationState, 
  sendMessage, 
  endSimulation,
  getInterimSummary,
  switchPersona,
  pollCoachFeedback,
  PERSONA_IMAGES,
  MEDDPICC_LABELS,
  PHASE_INFO,
  getTrustLevel
} from "@/lib/api";
import { ChatPanel } from "@/components/simulation/ChatPanel";
import { VoiceChatPanel } from "@/components/simulation/VoiceChatPanel";
import { MetricsPanel } from "@/components/simulation/MetricsPanel";
import { CoachPanel } from "@/components/simulation/CoachPanel";
import { InterimSummaryPanel } from "@/components/simulation/InterimSummaryPanel";
import { CompletionPanel } from "@/components/simulation/CompletionPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  TrendingUp, 
  LogOut, 
  BarChart3,
  Lightbulb,
  Users,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const SimulationPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [dealState, setDealState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [coachInterventions, setCoachInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [simView, setSimView] = useState("chat"); // "chat" | "interim" | "completed"
  const [interimSummary, setInterimSummary] = useState(null);
  const [interimLoading, setInterimLoading] = useState(false);
  const [endReason, setEndReason] = useState(null);
  
  const latestCoachFeedback = useRef(null);

  useEffect(() => {
    loadState();
  }, [sessionId]);

  const loadState = async () => {
    try {
      const response = await getSimulationState(sessionId);
      setDealState(response.data.deal_state);
      setMessages(response.data.messages);
      setCoachInterventions(response.data.coach_interventions);
    } catch (error) {
      console.error("Failed to load state:", error);
      toast.error("Failed to load simulation state");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (message) => {
    if (!message.trim() || sending) return;
    
    setSending(true);
    const userMsg = {
      role: "user",
      content: message,
      turn_number: (dealState?.session_clock || 0) + 1
    };
    setMessages(prev => [...prev, userMsg]);
    
    try {
      const response = await sendMessage(sessionId, message);
      const { persona_response, deal_state, phase_transitioned, new_phase, simulation_ended, end_reason, coach_feedback } = response.data;
      
      setDealState(deal_state);
      
      if (persona_response) {
        setMessages(prev => [...prev, persona_response]);
      }
      
      // Handle coach feedback: if included in response, use it directly
      // Otherwise, poll for it (background coach)
      if (coach_feedback) {
        setCoachInterventions(prev => [...prev, coach_feedback]);
        latestCoachFeedback.current = coach_feedback;
      } else if (response.data.coach_pending && !simulation_ended) {
        // Poll for coach feedback in background
        _pollForCoach(sessionId);
      }
      
      if (phase_transitioned) {
        toast.success(`Phase Transition: ${PHASE_INFO[new_phase]?.name || new_phase}`, {
          description: "You've advanced to the next phase!"
        });
      }
      
      if (simulation_ended) {
        setEndReason(end_reason);
        setSimView("completed");
      }
      
      // Check for plateau — trigger interim summary
      if (!simulation_ended && response.data.plateau_detected) {
        toast.info("Conversation appears to have plateaued", {
          description: "Generating summary and recommendations..."
        });
        handleShowInterimSummary();
      }
      
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const _pollForCoach = async (sid) => {
    const maxAttempts = 15;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 1000));
      try {
        const res = await pollCoachFeedback(sid);
        if (res.data.ready) {
          if (res.data.coach_feedback) {
            setCoachInterventions(prev => [...prev, res.data.coach_feedback]);
            latestCoachFeedback.current = res.data.coach_feedback;
          }
          // Update deal state with MEDDPICC updates from coach
          if (res.data.deal_state) {
            setDealState(res.data.deal_state);
          }
          // Check plateau
          if (res.data.plateau_detected) {
            toast.info("Conversation appears to have plateaued", {
              description: "Generating summary and recommendations..."
            });
            handleShowInterimSummary();
          }
          return;
        }
      } catch {
        // Polling error, continue
      }
    }
  };

  const handleEndSimulation = async () => {
    try {
      await endSimulation(sessionId);
      setEndReason("user_ended");
      setSimView("completed");
    } catch (error) {
      console.error("Failed to end simulation:", error);
      toast.error("Failed to end simulation");
    }
    setShowEndDialog(false);
  };

  const handleVoiceChatResponse = (data) => {
    const { persona_response, coach_feedback, deal_state, phase_transitioned, new_phase, simulation_ended, end_reason, transcribed_text } = data;

    setDealState(deal_state);

    if (transcribed_text) {
      setMessages(prev => [...prev, {
        role: "user",
        content: transcribed_text,
        turn_number: deal_state?.session_clock || 0
      }]);
    }
    if (persona_response) {
      setMessages(prev => [...prev, persona_response]);
    }

    if (coach_feedback) {
      setCoachInterventions(prev => [...prev, coach_feedback]);
      latestCoachFeedback.current = coach_feedback;
    }

    if (phase_transitioned) {
      toast.success(`Phase Transition: ${PHASE_INFO[new_phase]?.name || new_phase}`, {
        description: "You've advanced to the next phase!"
      });
    }

    if (simulation_ended) {
      setEndReason(end_reason);
      // Don't switch immediately - let voice playback finish, then show completion
      setSimView("completed");
    }
    
    // Check for plateau in voice mode too
    if (!simulation_ended && data.plateau_detected) {
      toast.info("Conversation appears to have plateaued", {
        description: "Generating summary and recommendations..."
      });
      handleShowInterimSummary();
    }
  };

  const handleShowInterimSummary = async () => {
    setInterimLoading(true);
    setSimView("interim");
    try {
      const response = await getInterimSummary(sessionId);
      setInterimSummary(response.data.summary);
      setDealState(response.data.deal_state);
    } catch (error) {
      console.error("Failed to get interim summary:", error);
      toast.error("Failed to generate summary");
      setSimView("chat");
    } finally {
      setInterimLoading(false);
    }
  };

  const handleSwitchPersona = async (targetPersona) => {
    try {
      const response = await switchPersona(sessionId, targetPersona);
      const { greeting, deal_state } = response.data;
      
      setDealState(deal_state);
      
      // Add the system message and greeting to messages
      setMessages(prev => [...prev, 
        {
          role: "system",
          content: `Conversation transitioned to ${response.data.new_persona_name}`,
        },
        greeting
      ]);
      
      setInterimSummary(null);
      setSimView("chat");
      setVoiceMode(false); // Switch back to text for new persona
      
      toast.success(`Now speaking with ${response.data.new_persona_name}`, {
        description: "A new stakeholder has joined the conversation."
      });
    } catch (error) {
      console.error("Failed to switch persona:", error);
      toast.error("Failed to switch stakeholder");
    }
  };

  const handleContinueCurrent = () => {
    setInterimSummary(null);
    setSimView("chat");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-400">Loading simulation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Header with bold accent border */}
      <header className="bg-white border-b border-slate-100 flex-shrink-0 accent-border-top">
        <div className="px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base font-bold text-slate-900">Deal Velocity</h1>
              </div>
              <Badge 
                className={`ml-1 text-xs ${
                  dealState?.phase === "P1" ? "phase-p1" :
                  dealState?.phase === "P2" ? "phase-p2" :
                  dealState?.phase === "P3" ? "phase-p3" :
                  dealState?.phase === "P4" ? "phase-p4" :
                  dealState?.phase === "COMPLETE" ? "phase-complete" :
                  "phase-failed"
                }`}
                data-testid="phase-badge"
              >
                {dealState?.phase_name || dealState?.phase}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Mobile panel toggles */}
              <div className="flex lg:hidden gap-1">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1 border-slate-200">
                      <BarChart3 className="w-4 h-4" />
                      <span className="hidden sm:inline">Metrics</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[300px] p-0">
                    <MetricsPanel dealState={dealState} />
                  </SheetContent>
                </Sheet>
                
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1 border-slate-200 relative">
                      <Lightbulb className="w-4 h-4" />
                      <span className="hidden sm:inline">Coach</span>
                      {coachInterventions.filter(c => !c.acknowledged).length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                          {coachInterventions.filter(c => !c.acknowledged).length}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[320px] p-0">
                    <CoachPanel 
                      interventions={coachInterventions} 
                      latestFeedback={latestCoachFeedback.current}
                    />
                  </SheetContent>
                </Sheet>
              </div>
              
              {/* Turn counter */}
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-md border border-slate-100">
                <span className="text-xs text-slate-400">Turn</span>
                <span className="text-sm font-semibold text-slate-800" data-testid="turn-counter">
                  {dealState?.session_clock || 0}/{dealState?.max_turns || 40}
                </span>
              </div>

              {/* Switch Stakeholder button — show when there are other personas available */}
              {dealState?.is_active && (simView === "chat" || voiceMode) && (() => {
                const allPersonas = ["jamie_sterling", "evelyn_reed", "ben_carter"];
                const available = allPersonas.filter(
                  p => p !== dealState?.active_persona && !(dealState?.completed_personas || []).includes(p)
                );
                return available.length > 0 && (dealState?.session_clock || 0) >= 3;
              })() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShowInterimSummary}
                  className="border-primary/30 text-primary hover:bg-primary/5"
                  data-testid="switch-stakeholder-btn"
                >
                  <Users className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Switch Stakeholder</span>
                  <span className="sm:hidden">Switch</span>
                </Button>
              )}
              
              {/* End button */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowEndDialog(true)}
                className="text-slate-500 hover:text-red-600 hover:border-red-200 border-slate-200"
                data-testid="end-simulation-btn"
              >
                <LogOut className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">End</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 overflow-hidden">
        <div className="simulation-shell h-full">
          {/* Left Panel - Metrics */}
          <div className="hidden lg:block border-r border-slate-100 bg-white overflow-y-auto">
            <MetricsPanel dealState={dealState} />
          </div>
          
          {/* Center Panel - Chat / Voice / Interim Summary / Completed */}
          <div className="flex flex-col bg-slate-50/50 overflow-hidden">
            {simView === "completed" ? (
              <CompletionPanel
                sessionId={sessionId}
                dealState={dealState}
                endReason={endReason}
              />
            ) : simView === "interim" ? (
              <InterimSummaryPanel
                summary={interimSummary}
                currentPersona={dealState?.active_persona}
                dealState={dealState}
                onSwitchPersona={handleSwitchPersona}
                onContinue={handleContinueCurrent}
                onEndSimulation={() => setShowEndDialog(true)}
                loading={interimLoading}
              />
            ) : voiceMode ? (
              <VoiceChatPanel
                dealState={dealState}
                messages={messages}
                onVoiceChatResponse={handleVoiceChatResponse}
                onSwitchToText={() => setVoiceMode(false)}
              />
            ) : (
              <ChatPanel 
                messages={messages}
                onSendMessage={handleSendMessage}
                sending={sending}
                dealState={dealState}
                onSwitchToVoice={() => setVoiceMode(true)}
              />
            )}
          </div>
          
          {/* Right Panel - Coach */}
          <div className="hidden lg:block border-l border-slate-100 bg-white overflow-y-auto">
            <CoachPanel 
              interventions={coachInterventions}
              latestFeedback={latestCoachFeedback.current}
            />
          </div>
        </div>
      </div>

      {/* End Simulation Dialog */}
      <AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Simulation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end the simulation? You will be taken to your evaluation report.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Simulation</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleEndSimulation}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-end-btn"
            >
              End & View Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
